import { forwardRef, useContext, type ComponentPropsWithoutRef } from "react";
import { DetailsContext } from "./internal/DetailsContext";

import cx from "classnames";
import styles from "./Details.module.scss";

/**
 * Props for the DetailsContent component
 * @extends ComponentPropsWithoutRef<"div">
 */
export type DetailsContentProps = ComponentPropsWithoutRef<"div"> & {
  /**
   * Fixed height for the content area
   */
  height?: string | number;
  /**
   * Maximum height for the content area
   */
  maxHeight?: string | number;
};

/**
 * DetailsContent component for the expandable content area of a Details section.
 *
 * Features:
 * - Expandable and collapsible content area
 * - Smooth animations for height transitions
 * - Configurable height and maxHeight constraints
 * - Automatic overflow handling when height is constrained
 * - Proper ARIA associations with the summary
 * - Responsive design with flexible layout
 *
 * @example
 * <Details.Content maxHeight={300}>
 *   <p>This content will be constrained to 300px max height</p>
 * </Details.Content>
 */
export const DetailsContent = forwardRef<HTMLDivElement, DetailsContentProps>(
  function DetailsContent(
    {
      className: classNameProp,
      children,
      height,
      maxHeight,
      style: styleProp,
      ...rest
    },
    ref,
  ) {
    const { id, isExpanded } = useContext(DetailsContext);

    const style = {
      height,
      maxHeight,
      ...(height || maxHeight ? { overflow: "auto" } : {}),
      ...styleProp,
    };

    return (
      <div
        data-anv="details-content"
        id={id}
        className={cx(styles["content-wrapper"], {
          [styles.expanded]: isExpanded,
        })}
        ref={ref}
      >
        <div className={styles.content}>
          <div className={classNameProp} style={style} {...rest}>
            {children}
          </div>
        </div>
      </div>
    );
  },
);
